import Helper from '../../../../Common/Helper';
import { appContext } from '../../../../AppContext';
import { IbssComponent } from '../../../../Components/Core/BaseComponent/IbssComponent';
import { JSX } from 'react';
import { SerendieSymbol } from '@serendie/symbols';
import EditSolutionParameterDialog from './EditSolutionParameterDialog';
import { OntologyNode, SolutionParameter } from './DataModels';
import { BuiltInType } from '../../../../Providers.FadxPlatformApiClientV2/FadxPlatformApiClientV2';
import { PagedResponse } from "@iconics/core";
import { Guid } from '@iconics/core';
import { Button, ButtonBar, Card, DataGrid, DeleteButton, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, OnSectionUpdateHandler, RightButtons } from '@iconics/components';
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";

class EditSolutionParameters extends IbssComponent<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }
    private languageValuesHelper: LanguageValuesHelper;

    private userCanCreate: boolean;
    private userCanUpdate: boolean;
    private userCanDelete: boolean;
    private userCanEnable: boolean;
    private userCanDisable: boolean;

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state =
        {
            ontologyNodes: [],
            grid: { rows: [], checkedIds: [] },
            gridRefreshToken: Guid.new().toString(),
            showEditNodeDialog: false,
            selectedSolutionParameter: null,
        };

        this.userCanCreate = this.local.hasRight('DATAMODEL.HierarchyParameters.Create');
        this.userCanUpdate = this.local.hasRight('DATAMODEL.HierarchyParameters.Update');
        this.userCanDelete = this.local.hasRight('DATAMODEL.HierarchyParameters.Delete');
        this.userCanEnable = this.local.hasRight('API.HierarchyParameters.Enable');
        this.userCanDisable = this.local.hasRight('API.HierarchyParameters.Disable');
    }

    public async componentDidMount(): Promise<void>
    {
        await this.loadOntologyNodes();
    }

    public async componentDidUpdate(prevProps: IProps): Promise<void>
    {
        if (prevProps.ontologyId != this.props.ontologyId)
        {
            await this.loadOntologyNodes();
        }
    }

    private async loadOntologyNodes(): Promise<void>
    {
        if (!this.props.ontologyId)
        {
            this.setState({ ontologyNodes: [] });
            return;
        }

        const ontologyNodeData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.nodes.get<PagedResponse<OntologyNode[]>>({
            tenancy: appContext().tenantId,
            id: this.props.ontologyId,
        });

        const ontologyNodes = ontologyNodeData.value.map(x => ({
            id: x.Id,
            name: x.Name,
            etag: x['@odata.etag'],
            isEnabled: x.IsEnabled,
        }));

        this.setState({ ontologyNodes: ontologyNodes });
    }

    private solutionParameters?: ISolutionParameter[];
    private async getSolutionParameters(): Promise<ISolutionParameter[]>
    {
        if (!this.solutionParameters)
        {
            const response = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.byId.parameters.get<SolutionParameter[]>({
                tenancy: appContext().tenantId,
                id: this.props.solutionId,
            });

            this.solutionParameters = response.map(x => ({
                id: x.Id,
                name: x.Name,
                displayName: this.languageValuesHelper.fromString(x.DisplayName),
                ontologyNodeId: x.OntologyNode_ID,
                valueType: x.ValueType,
                isEnabled: x.IsEnabled,
                concurrencyStamp: x['@odata.etag']
            }));
        }
        return this.solutionParameters;
    }

    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            let index = 0;
            while (index < this.checkedIds.length)
            {
                const id = this.checkedIds[index];
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.byId.parameters.byParameterId.delete({
                    tenancy: appContext().tenantId,
                    id: this.props.solutionId,
                    parameterId: id,
                });
                index = index + 1;
            }
            this.refreshGrid();
        });
    }

    private async handleEnable(): Promise<void>
    {
        await this.loading(async () =>
        {
            for (const param of this.selectedSolutionsParameters)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.byId.parameters.byParameterId.enable.patch({
                    tenancy: appContext().tenantId,
                    id: this.props.solutionId,
                    parameterId: param.id,
                    body: { '@odata.etag': param.concurrencyStamp }
                });
            }
            this.refreshGrid();
        });
    }

    private async handleDisable(): Promise<void>
    {
        await this.loading(async () =>
        {
            for (const param of this.selectedSolutionsParameters)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.byId.parameters.byParameterId.disable.patch({
                    tenancy: appContext().tenantId,
                    id: this.props.solutionId,
                    parameterId: param.id,
                    body: { '@odata.etag': param.concurrencyStamp }
                });
            }
            this.refreshGrid();
        });
    }

    private handleEdit(solutionParameterId: string): React.ReactNode
    {
        if (!this.userCanUpdate)
        {
            return;
        }

        const solutionParameter = this.state.grid.rows.find(i => i.id == solutionParameterId);

        this.setState({
            showEditNodeDialog: true,
            selectedSolutionParameter: solutionParameter ?? null,
        });
    }

    private handleSaveSolutionParameter(): void
    {
        this.setState({ showEditNodeDialog: false });
        this.refreshGrid();
    }

    private get selectedSolutionsParameters(): ISolutionParameter[]
    {
        const solutions = this.checkedIds
            .map(id => this.state.grid.rows.find(i => i.id === id))
            .filter(i => !!i) as ISolutionParameter[];

        return solutions;
    }

    private get checkedIds(): string[]
    {
        return this.state.grid.checkedIds ?? [];
    }

    private refreshGrid(): void
    {
        this.solutionParameters = undefined;
        this.setState({ gridRefreshToken: Guid.new().toString() });
    }

    private async handleRowsRequest(request: IGridRowsRequest<ISolutionParameter>): Promise<IGridRowsResponse<ISolutionParameter>>
    {
        const rows = await this.getSolutionParameters();
        const sortedRows = DataGrid.sort(rows, request.sorter);
        return { rows: sortedRows, nextSkipToken: '' };
    }

    public render(): JSX.Element
    {
        return (
            <Card
                title={this.labels.Platform_Solution_MetaDataListTitle}
                subTitle={this.labels.Platform_Solution_MetaDataListSubTitle}
                onSectionUpdate={i => this.props.onSectionUpdate(i)}
            >
                <GridContainer>
                    <GridItem>
                        <ButtonBar>
                            <RightButtons>
                                <DeleteButton
                                    small={true}
                                    onDelete={() => this.handleDelete()}
                                    dialogMessage={this.labels.Platform_SolutionParameter_ConfirmDeleteMsg}
                                    disabled={!this.userCanDelete || this.checkedIds.length !== 1}
                                />
                                <Button
                                    size='small'
                                    styleType='ghost'
                                    leftIcon={<SerendieSymbol name='close' />}
                                    onClick={() => this.handleDisable()}
                                    disabled={!this.userCanDisable || this.checkedIds.length !== 1}
                                >
                                    {this.labels.Platform_CommonAction_Disable}
                                </Button>
                                <Button
                                    size='small'
                                    styleType='ghost'
                                    leftIcon={<SerendieSymbol name='check' />}
                                    onClick={() => this.handleEnable()}
                                    disabled={!this.userCanEnable || this.checkedIds.length !== 1}
                                >
                                    {this.labels.Platform_CommonAction_Enable}
                                </Button>
                                <Button
                                    size='small'
                                    styleType='ghost'
                                    leftIcon={<SerendieSymbol name='pencil' />}
                                    onClick={() => this.handleEdit(this.checkedIds[0])}
                                    disabled={!this.userCanUpdate || this.checkedIds.length !== 1}
                                >
                                    {this.labels.Platform_CommonAction_Edit}
                                </Button>
                                <Button
                                    size='small'
                                    styleType='ghost'
                                    leftIcon={<SerendieSymbol name='plus' />}
                                    onClick={() => this.setState({ showEditNodeDialog: true, selectedSolutionParameter: null })}
                                    disabled={!this.userCanCreate || this.checkedIds.length !== 0}
                                >
                                    {this.labels.Platform_CommonAction_Add}
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </GridItem>
                    <GridItem>
                        <DataGrid
                            columns={[
                                {
                                    columnId: Helper.nameOf<ISolutionParameter>("ontologyNodeId"),
                                    title: this.labels.HierarchyParameter_OntologyNode_Name,
                                    getCellValue: i => this.state.ontologyNodes.find(x => x.id === i.ontologyNodeId)?.name ?? '',
                                    columnWidth: 25,
                                },
                                {
                                    columnId: Helper.nameOf<ISolutionParameter>("name"),
                                    title: this.labels.HierarchyParameter_Name,
                                    getCellValue: i => i.name,
                                    columnWidth: 25,
                                    sortable: true,
                                },
                                {
                                    columnId: Helper.nameOf<ISolutionParameter>("valueType"),
                                    title: this.labels.HierarchyParameter_ValueType,
                                    getCellValue: i => i.valueType,
                                    columnWidth: 25,
                                    sortable: true,
                                },
                                {
                                    columnId: Helper.nameOf<ISolutionParameter>("isEnabled"),
                                    title: this.labels.HierarchyParameter_IsEnabled,
                                    getCellValue: i =>
                                        <SerendieSymbol
                                            name={i.isEnabled === 'Yes' ? 'check-circle' : 'close-circle'}
                                            style={{ marginLeft: '32px' }}
                                        />,
                                    columnWidth: 25,
                                    sortable: true,
                                },
                            ]}
                            data={this.state.grid}
                            onChange={i => this.setState({ grid: i })}
                            rowId={i => i.id}
                            onRowsRequest={i => this.handleRowsRequest(i)}
                            onCellClicked={i => this.handleEdit(i.id)}
                            showCheckBoxes={true}
                            allowOnlyOneCheckBox={true}
                            refreshToken={this.state.gridRefreshToken}
                        />
                    </GridItem>
                </GridContainer>
                <EditSolutionParameterDialog
                    open={this.state.showEditNodeDialog}
                    onClose={() => this.setState({ showEditNodeDialog: false, selectedSolutionParameter: null })}
                    ontologyNodes={this.state.ontologyNodes}
                    selectedSolutionParameter={this.state.selectedSolutionParameter}
                    onSave={() => this.handleSaveSolutionParameter()}
                    solutionId={this.props.solutionId}
                />
            </Card>
        )
    }
}

export default EditSolutionParameters;

export interface IProps
{
    solutionId: string;
    ontologyId: string;
    onSectionUpdate: OnSectionUpdateHandler;
}

export interface IState
{
    ontologyNodes: IOntologyNode[];
    grid: IGridData<ISolutionParameter>;
    gridRefreshToken: string;
    showEditNodeDialog: boolean,
    selectedSolutionParameter: ISolutionParameter | null,
}

export interface ISolutionParameter
{
    id: string;
    name: string,
    displayName: ILanguageValues,
    valueType: BuiltInType,
    isEnabled: string,
    ontologyNodeId: string,
    concurrencyStamp: string
}

export interface IOntologyNode
{
    id: string;
    name: string;
    isEnabled: string;
    etag: string;
}
